import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { tokens } from '@companycam/slab';
import ReactSelect from 'react-select';
import { useMutation, useLazyQuery } from '@apollo/client';
import { get } from 'lodash';
import * as immutable from 'object-path-immutable';

import { REACT_SELECT_STYLES } from 'helpers/styles';
import toast from 'helpers/toast';
import { graphQLIdGenerator } from 'helpers/graphql-utils';
import { TextInput, TextArea } from 'components/core/forms';
import Spinner from 'components/shared/DesignSystem/Spinner';
import SectionEntry from './SectionEntry';
import {
  CREATE_REPORT_NODE,
  CREATE_REPORT_NODE_BULK,
  GET_REPORT,
} from '../gql/reports.graphql';
import useGetReportsForModal from './useGetReportsForModal';
import { Button } from '@companycam/slab-web';

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr auto;
  grid-template-areas:
    'header'
    'main'
    'footer';
  flex: 1;
  padding: ${tokens.size_spacing_xl}px;
`;

const Header = styled.div`
  grid-area: header;
  display: flex;
  align-items: center;
  /* padding: ${tokens.size_spacing_m}px ${tokens.size_spacing_l}px; */
  border-bottom: ${tokens.size_border_width_s}px solid
    ${tokens.color_border_default_le};
`;

const Main = styled.div`
  grid-area: main;
  overflow-y: auto;
  margin-top: ${tokens.size_spacing_xl}px;
`;

const Footer = styled.div`
  grid-area: footer;
  &.btn-container {
    position: sticky;
    bottom: 0;
  }
`;

const Title = styled.h3`
  /* margin: 0 !important; */
  flex: 1;
  padding-inline-end: ${tokens.size_spacing_m}px;
`;

const CloseButton = styled.button``;

const FormContent = styled.div``;

const FormRow = styled.div`
  margin: 0 0 ${tokens.size_spacing_l}px;
`;

const EntriesContainer = styled.div`
  counter-reset: block-counter;
  h3 {
    text-align: center;
  }
`;

const Menu = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  padding: 20px 0px;
`;

const InsertButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${tokens.color_background_1_le};
  border: ${tokens.size_border_width_m}px solid
    ${tokens.color_border_default_le};
  box-sizing: border-box;
  border-radius: 9999em;
  padding: 6px 16px 6px 8px;
  color: ${(props) =>
    props.isSelected
      ? tokens.color_link_primary_le
      : tokens.color_link_subtle_le};
  transition: all 0.25s ease-out;
  cursor: pointer;
  &:hover,
  &:focus {
    color: ${tokens.color_link_primary_le};
    box-shadow: 0 ${tokens.size_depth_2_y}px ${tokens.size_depth_2_blur}px
      ${tokens.color_depth_2_le};
  }
`;

const InsertIcon = styled.i`
  font-size: 28px;
`;

const ButtonLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${tokens.color_link_le_subtle};
  margin-inline-start: ${tokens.size_spacing_s}px;
`;

const InsertPhotosButton = ({ onClick, isSelected }) => (
  <InsertButton
    type="button"
    onClick={onClick}
    className="hint--right"
    aria-label="Insert content here"
    isSelected={isSelected}
  >
    <InsertIcon
      className={`mdi ${
        isSelected ? 'mdi-checkbox-marked-circle' : 'mdi-radiobox-blank'
      }`}
    />
    <ButtonLabel>Insert Here</ButtonLabel>
  </InsertButton>
);

const databaseIdFrom = (obj) => parseInt(obj.databaseId, 10);
const imageNodeForId = (imageId) => {
  return graphQLIdGenerator('Image', imageId);
};

const AddToReportModal = ({ onRequestClose, assets }) => {
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [sectionOptions, setSectionOptions] = useState([]);
  const [isCreatingSection, setIsCreatingSection] = useState(false);
  const [createTitle, setCreateTitle] = useState(null);
  const [createSummary, setCreateSummary] = useState(null);
  const [sectionEntries, setSectionEntries] = useState(null);
  const [fetchMoreLoading, setFetchMoreLoading] = useState(false);
  const [shouldFetchSections, setShouldFetchSections] = useState(false);
  const [entries, setEntries] = useState([]);
  const [isAddingPhoto, setIsAddingPhoto] = useState(false);
  const selectedItems = get(assets, 'assetsByType.photos', []);
  const { reports, reportsLoading, fetchMoreReports } = useGetReportsForModal();

  const formattedReportData = reports.map((report) => ({
    label: report.node.title,
    value: report.node.id,
    ...report.node,
  }));

  const DEFAULT_PAGEINFO = { hasNextPage: false, endCursor: null };

  const pageInfoFromData = (pData) =>
    get(pData, 'viewer.report.reportNodes.pageInfo', DEFAULT_PAGEINFO);

  const getReportNodes = (rData) =>
    get(rData, 'viewer.report.reportNodes.nodes', []);

  const mergeReportNodes = (oldData, { fetchMoreResult: newData }) => {
    if (!newData) {
      return oldData;
    }
    const oldReportNodes = getReportNodes(oldData);
    const newReportNodes = getReportNodes(newData);
    const combinedReportNodes = [...oldReportNodes, ...newReportNodes];
    return immutable.set(
      newData,
      'viewer.report.reportNodes.nodes',
      combinedReportNodes,
    );
  };

  const [
    getReport,
    { fetchMore, loading: getReportLoading, data: sectionData },
  ] = useLazyQuery(GET_REPORT, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'none',
    onCompleted: (rData) => {
      const pageInfo = pageInfoFromData(rData);

      setFetchMoreLoading(true);

      if (pageInfo.hasNextPage) {
        fetchMore({
          variables: { id: selectedReport.id, after: pageInfo.endCursor },
          updateQuery: mergeReportNodes,
        });
      } else {
        setFetchMoreLoading(false);
      }
    },
  });

  useEffect(() => {
    const setSections = async () => {
      if (selectedReport !== null && shouldFetchSections === true) {
        await getReport({ variables: { id: selectedReport.id } });
        setShouldFetchSections(false);
      }
    };

    setSections();
  }, [selectedReport, getReport, shouldFetchSections]);

  useEffect(() => {
    if (sectionData) {
      const reportNodes = sectionData.viewer.report.reportNodes.nodes;
      const sections = reportNodes.filter(
        (node) => node.type === 'ASSET_SECTION',
      );
      sections.sort((a, b) => a.position - b.position);

      const assetSections = reportNodes.filter(
        (node) => node.type === 'ASSET_SECTION',
      );
      const newSectionOption = {
        label: 'Create A New Section',
        value: 'CreateNewSection',
      };

      const formattedSectionOptions = assetSections.map((section) => ({
        label: section.name,
        value: section.id,
        ...section,
      }));

      setSectionOptions([newSectionOption, ...formattedSectionOptions]);

      const sectionNodes = reportNodes.filter(
        (node) => node.type === 'ASSET_CONTAINER',
      );

      sectionNodes.sort((a, b) => a.position - b.position);

      setSectionEntries(sectionNodes);
    }
  }, [sectionData]);

  const [createReportNodes] = useMutation(CREATE_REPORT_NODE_BULK, {
    onError: () => {
      toast.error('There was a problem adding item to report');
    },
  });

  const [createSection] = useMutation(CREATE_REPORT_NODE, {
    onError: () => {
      toast.error('There was a problem creating the section');
    },
  });

  useEffect(() => {
    if (sectionEntries && selectedSection) {
      const filteredEntries = sectionEntries.filter(
        (sectionNode) => sectionNode.parent.id === selectedSection.id,
      );

      if (filteredEntries.length === 0) {
        setSelectedPosition(1);
      }

      setEntries(filteredEntries);
    }
  }, [sectionEntries, selectedSection]);

  const handleMoveItems = async (newSectionId) => {
    const photoDatabaseIds = selectedItems.map(databaseIdFrom);
    const imageIds = photoDatabaseIds.map(imageNodeForId);
    setIsAddingPhoto(true);

    createReportNodes({
      variables: {
        input: {
          id: selectedReport.id,
          position: selectedPosition,
          imageIds,
          parentId: newSectionId || selectedSection.id,
        },
      },
    }).then(() => {
      Turbo.visit(
        `/reports/${selectedReport.id}/edit/${
          newSectionId || selectedSection.id
        }`,
      );
    });
  };

  const handleCreateSection = async () => {
    await createSection({
      variables: {
        input: {
          type: 'ASSET_SECTION',
          name: createTitle,
          position: sectionOptions.length + 1,
          reportId: selectedReport.id,
          data: {
            assetSection: {
              summary: createSummary,
            },
          },
        },
      },
    }).then((result) => {
      const newSection = get(
        result,
        'data.reportNodeCreate.reportNode' || null,
      );
      if (newSection) {
        handleMoveItems(newSection.id);
      }
    });
  };

  const handleCreateSectionAndMoveItems = () => {
    handleCreateSection();
  };

  const saveAction = () => {
    if (isCreatingSection) {
      handleCreateSectionAndMoveItems();
    } else {
      handleMoveItems();
    }
  };

  const handleReportSelect = (selected) => {
    setSelectedReport(selected);
    setShouldFetchSections(true);
    setSelectedSection(null);
    setSelectedPosition(null);
    setIsCreatingSection(false);
  };

  const handleSectionSelect = (selected) => {
    setSelectedSection(selected);
    setSelectedPosition(null);
    setIsCreatingSection(selected.value === 'CreateNewSection');
  };

  const renderCreateSectionForm = () => {
    return (
      <>
        <FormRow className="input-wrap">
          <label htmlFor="assetReportSection">Section Title</label>
          <TextInput
            placeholder="Section Title"
            name="title"
            value={createTitle}
            onChange={(e) => setCreateTitle(e.target.value)}
          />
        </FormRow>
        <FormRow className="input-wrap">
          <TextArea
            name="subtitle"
            value={createSummary}
            onChange={(e) => setCreateSummary(e.target.value)}
            label="Section Summary"
          />
        </FormRow>
      </>
    );
  };

  const renderCreateMenu = (position = 0) => {
    return (
      <Menu>
        <InsertPhotosButton
          type="button"
          onClick={() => setSelectedPosition(position)}
          isSelected={position === selectedPosition}
        />
      </Menu>
    );
  };

  const renderEmptySectionCreate = () => {
    return null;
  };

  const renderSectionEntry = (entry, index) => {
    return (
      <React.Fragment key={entry.id}>
        {index === 0 && renderCreateMenu(index + 1)}
        <SectionEntry entry={entry} />
        {renderCreateMenu(index + 2)}
      </React.Fragment>
    );
  };

  const renderSectionLayout = () => {
    return (
      <EntriesContainer>
        {entries.length > 0
          ? entries.map(renderSectionEntry)
          : renderEmptySectionCreate()}
      </EntriesContainer>
    );
  };

  const sectionPlaceholder = () => {
    if (!selectedReport) {
      return null;
    } else if (getReportLoading || fetchMoreLoading) {
      return (
        <Spinner
          size="tiny"
          label="Loading Sections..."
          meterColor={tokens.color_link_primary_le}
        />
      );
    } else {
      return 'Select a Section...';
    }
  };

  return (
    <Modal
      ariaHideApp={false}
      contentLabel="Move Photos"
      isOpen
      overlayClassName="modal"
      className="modal-content-wrap rearrange-wizard is-padless"
    >
      <ContentWrapper>
        <Header>
          <Title className="modal-title">Add Photos to Report</Title>
          <CloseButton
            onClick={onRequestClose}
            type="button"
            className="modal-close-button"
          >
            <i size={40} className="mdi mdi-close" />
          </CloseButton>
        </Header>
        <Main>
          <FormContent>
            <FormRow>
              <label htmlFor="assetReport">Report</label>
              <ReactSelect
                id="assetReport"
                name="assetReport"
                styles={REACT_SELECT_STYLES}
                options={formattedReportData}
                onChange={(option) => {
                  handleReportSelect(option);
                }}
                onMenuScrollToBottom={fetchMoreReports}
                isLoading={reportsLoading}
                placeholder={
                  reportsLoading ? (
                    <Spinner
                      size="tiny"
                      label="Loading Reports..."
                      meterColor={tokens.color_link_primary_le}
                    />
                  ) : (
                    'Select a Report...'
                  )
                }
                value={selectedReport}
              />
            </FormRow>

            <FormRow>
              <label htmlFor="assetReportSection">Section</label>
              <ReactSelect
                id="assetReportSection"
                name="assetReportSection"
                styles={REACT_SELECT_STYLES}
                options={sectionOptions}
                onChange={(option) => {
                  handleSectionSelect(option);
                }}
                isDisabled={getReportLoading || !sectionData}
                value={selectedSection}
                placeholder={sectionPlaceholder()}
              />
            </FormRow>

            <FormRow>
              {isCreatingSection ? (
                <div>{renderCreateSectionForm()}</div>
              ) : null}
              {!isCreatingSection && selectedSection ? (
                <div>{renderSectionLayout()}</div>
              ) : null}
            </FormRow>
          </FormContent>
        </Main>

        <Footer className="btn-container">
          <Button
            color="primary"
            onClick={saveAction}
            loading={isAddingPhoto}
            disabled={!(selectedPosition || (isCreatingSection && createTitle))}
          >
            Add to Report
          </Button>
          <Button onClick={onRequestClose}>Cancel</Button>
        </Footer>
      </ContentWrapper>
    </Modal>
  );
};

InsertPhotosButton.propTypes = {
  onClick: PropTypes.func,
  isSelected: PropTypes.bool,
};

AddToReportModal.propTypes = {
  onRequestClose: PropTypes.func,
  assets: PropTypes.object,
};

export default AddToReportModal;
